import React, { useState, useEffect } from 'react';
import { NavLink, useNavigate } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import axios from 'axios';
import { 
  Clock, 
  LayoutDashboard, 
  Users, 
  MapPin, 
  FileSpreadsheet, 
  Bell,
  LogOut,
  Menu,
  X,
  Building2,
  Settings,
  CalendarDays,
  Sliders,
  HelpCircle,
  User,
  CreditCard,
  ClipboardList,
  FolderKanban,
  History,
  Shield
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Sheet, SheetContent, SheetTrigger } from '@/components/ui/sheet';
import NotificationPanel from '@/components/NotificationPanel';
import PushNotificationManager from '@/components/PushNotificationManager';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Admin-only nav items (hidden from managers)
const adminOnlyItems = [
  { path: '/admin/employees', icon: Users, label: 'Employees' },
  { path: '/admin/offices', icon: MapPin, label: 'Offices' },
  { path: '/admin/controls', icon: Sliders, label: 'Controls' },
  { path: '/admin/settings', icon: Settings, label: 'Settings' },
  { path: '/admin/billing', icon: CreditCard, label: 'Billing' },
];

// Manager-accessible nav items
const managerItems = [
  { path: '/admin', icon: LayoutDashboard, label: 'Dashboard', exact: true },
  { path: '/admin/timesheet', icon: ClipboardList, label: 'Timesheet' },
  { path: '/admin/roster', icon: CalendarDays, label: 'Roster' },
  { path: '/admin/projects', icon: FolderKanban, label: 'Projects' },
  { path: '/admin/reports', icon: FileSpreadsheet, label: 'Reports' },
  { path: '/admin/audit-logs', icon: History, label: 'Audit Logs' },
];

// Help item - available to both admins and managers
const helpItem = { path: '/admin/help', icon: HelpCircle, label: 'Help' };

// Security & Privacy - available to all
const securityItems = [
  { path: '/security', icon: Shield, label: 'Security' },
  { path: '/privacy', icon: User, label: 'Privacy' },
];

const CompanyAdminSidebar = () => {
  const { user, logout } = useAuth();
  
  // Determine which nav items to show based on role
  const isManager = user?.role === 'manager' || (user?.role === 'employee' && user?.is_manager);
  const isAdmin = user?.role === 'company_admin';
  
  // Managers see only manager items, admins see all
  // Add help item at the end for both
  const baseNavItems = isManager 
    ? managerItems 
    : [...managerItems.slice(0, 1), ...adminOnlyItems.slice(0, 2), ...managerItems.slice(1), ...adminOnlyItems.slice(2)];
  
  const navItems = [...baseNavItems, helpItem, ...securityItems];
  
  const navigate = useNavigate();
  const [unreadCount, setUnreadCount] = useState(0);
  const [showNotifications, setShowNotifications] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  useEffect(() => {
    const fetchUnreadCount = async () => {
      try {
        const response = await axios.get(`${API}/notifications/unread-count`);
        setUnreadCount(response.data.count);
      } catch (error) {
        console.error('Failed to fetch unread count:', error);
      }
    };
    fetchUnreadCount();
    const interval = setInterval(fetchUnreadCount, 30000);
    return () => clearInterval(interval);
  }, []);

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  const SidebarContent = ({ mobile = false }) => (
    <div className="flex flex-col h-full">
      {/* Logo */}
      <div className="p-6 border-b shrink-0">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-xl bg-[hsl(243,65%,20%)] flex items-center justify-center text-white">
            <Clock className="w-5 h-5" />
          </div>
          <div>
            <span className="text-lg font-bold font-['Manrope'] text-[hsl(243,65%,20%)]">ClockIn Pro</span>
            <p className="text-xs text-slate-500 flex items-center gap-1">
              <Building2 className="w-3 h-3" />
              {user?.company_name}
            </p>
          </div>
        </div>
      </div>

      {/* Navigation - scrollable area */}
      <nav className="flex-1 p-4 space-y-1 overflow-y-auto">
        {navItems.map((item) => (
          <NavLink
            key={item.path}
            to={item.path}
            end={item.exact}
            onClick={() => mobile && setMobileMenuOpen(false)}
            className={({ isActive }) => `sidebar-link ${isActive ? 'active' : ''}`}
            data-testid={`nav-${item.label.toLowerCase()}`}
          >
            <item.icon className="w-5 h-5" />
            <span>{item.label}</span>
          </NavLink>
        ))}
        
        {/* Admin/Manager clock-in link - both can clock in */}
        {(isManager || isAdmin) && (
          <>
            <div className="pt-4 pb-2">
              <p className="px-4 text-xs font-semibold text-slate-400 uppercase tracking-wider">My Time</p>
            </div>
            <NavLink
              to="/dashboard"
              onClick={() => mobile && setMobileMenuOpen(false)}
              className={({ isActive }) => `sidebar-link ${isActive ? 'active' : ''}`}
              data-testid="nav-my-clock"
            >
              <Clock className="w-5 h-5" />
              <span>My Clock-In</span>
            </NavLink>
          </>
        )}
      </nav>

      {/* User Section - always visible at bottom */}
      <div className="p-4 border-t space-y-2 shrink-0 bg-white">
        <div className="flex items-center gap-2">
          <Button 
            variant="ghost" 
            className="flex-1 justify-start gap-3 relative"
            onClick={() => setShowNotifications(true)}
            data-testid="admin-notification-btn"
          >
            <Bell className="w-5 h-5" />
            <span>Notifications</span>
            {unreadCount > 0 && (
              <span className="ml-auto bg-[hsl(var(--destructive))] text-white text-xs rounded-full px-2 py-0.5">
                {unreadCount}
              </span>
            )}
          </Button>
          <PushNotificationManager />
        </div>
        <div className="flex items-center gap-3 px-4 py-3">
          <div className="employee-avatar text-sm">
            {user?.name?.charAt(0)?.toUpperCase()}
          </div>
          <div className="flex-1 min-w-0">
            <p className="font-medium text-sm truncate">{user?.name}</p>
            <p className="text-xs text-slate-500">{isManager ? 'Manager' : 'Company Admin'}</p>
          </div>
        </div>
        <NavLink to="/profile">
          <Button 
            variant="ghost" 
            className="w-full justify-start gap-3 text-slate-600 hover:text-indigo-600 hover:bg-indigo-50"
            data-testid="admin-profile-btn"
          >
            <User className="w-5 h-5" />
            <span>My Profile</span>
          </Button>
        </NavLink>
        <Button 
          variant="ghost" 
          className="w-full justify-start gap-3 text-red-600 hover:text-red-700 hover:bg-red-50"
          onClick={handleLogout}
          data-testid="admin-logout-btn"
        >
          <LogOut className="w-5 h-5" />
          <span>Log Out</span>
        </Button>
      </div>
    </div>
  );

  return (
    <>
      {/* Desktop Sidebar */}
      <aside className="sidebar hidden md:flex">
        <SidebarContent />
      </aside>

      {/* Mobile Header */}
      <header className="glass-header md:hidden">
        <div className="px-4 py-3 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-8 h-8 rounded-lg bg-[hsl(243,65%,20%)] flex items-center justify-center text-white">
              <Clock className="w-4 h-4" />
            </div>
            <span className="font-bold font-['Manrope'] text-[hsl(243,65%,20%)]">ClockIn Pro</span>
          </div>
          <div className="flex items-center gap-2">
            <Button 
              variant="ghost" 
              size="icon"
              className="relative"
              onClick={() => setShowNotifications(true)}
            >
              <Bell className="w-5 h-5" />
              {unreadCount > 0 && (
                <span className="notification-badge">{unreadCount}</span>
              )}
            </Button>
            <Sheet open={mobileMenuOpen} onOpenChange={setMobileMenuOpen}>
              <SheetTrigger asChild>
                <Button variant="ghost" size="icon">
                  {mobileMenuOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
                </Button>
              </SheetTrigger>
              <SheetContent side="left" className="p-0 w-64">
                <SidebarContent mobile />
              </SheetContent>
            </Sheet>
          </div>
        </div>
      </header>

      {/* Notification Panel */}
      <NotificationPanel 
        open={showNotifications} 
        onClose={() => setShowNotifications(false)}
        onRead={() => setUnreadCount(prev => Math.max(0, prev - 1))}
      />
    </>
  );
};

export default CompanyAdminSidebar;
